import { Inbox, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { T, tt } from '@agos/shared'

import { cn } from '../cn'

// ─── EmptyState ──────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon?: ReactNode
  title?: string
  description?: string
  action?: ReactNode
  className?: string
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-16 text-center', className)}>
      <div className="mb-3 text-muted-foreground/40">{icon ?? <Inbox className="w-12 h-12" />}</div>
      {title && <p className="text-sm font-semibold text-foreground mb-1">{title}</p>}
      <p className="text-sm text-muted-foreground max-w-sm">
        {/* FB-006: legacy reads "Ziadne data na zobrazenie" - Slovak with its
            diacritics stripped. sk.json spells it correctly, so the correct
            spelling is what ships, through the translation mechanism. */}
        {description ?? <T>Žiadne dáta na zobrazenie</T>}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

// ─── ReadFailed ──────────────────────────────────────────────────────────────

interface ReadFailedProps {
  children: ReactNode
  /**
   * The `data-*` name this banner answers to, without the prefix - `"stats-error"` gives
   * `data-stats-error`.
   *
   * **Optional, and one caller really has none.** `MarketingPage`'s copy carries
   * `data-marketing-error` on its `<main>` wrapper rather than on the banner, and §3.149's table listed
   * that name against the file. A conversion that trusted the table would have moved it onto this
   * element - off the one `tower.spec.ts` selects and onto a descendant - which fails as *the banner
   * works and the test is wrong*. So a caller may pass nothing.
   */
  marker?: string
  className?: string
}

/**
 * A read that did not answer, said once - `SAAS.md` §3.167, FB-918.
 *
 * <p><b>Twenty-one hand-written copies of one `<p>`, and only two of them were announced.</b> §3.149
 * recorded the duplication as a maintenance debt at nineteen; the measurement that matters is that **2 of 21
 * carried `role="alert"`**, so nineteen of the Tower's error messages were invisible to a screen reader and
 * the operator had to notice red text. **The duplication is the cause**: the attribute was added to two
 * copies and there was nothing for it to reach the rest through.
 *
 * <p><b>Twenty-one rather than twenty, and the difference is the lesson.</b> The conversion swept for the
 * exact class string and found 20 in 17 files; there were **two** distinct strings, because
 * `MarketingPage.tsx`'s refusal banner spells the padding `px-3 py-2` where the other twenty use `p-3`. That
 * copy was invisible to the sweep, so its `data-marketing-refused` was dropped - and the check that reported
 * *markers lost: none* used the same pattern, so it agreed.
 * `agos-a-ratchets-pattern-is-its-blind-spot`, caught by asking git what the twenty-one looked like rather
 * than by asking the sweep again.
 *
 * <p><b>That outlier is normalised here rather than carried</b>, so the twenty-one become one paragraph with
 * one padding. The visible change is two pixels of vertical padding on one screen's refusal notice, which is
 * a smaller price than a component whose first prop exists to preserve a difference nobody chose.
 *
 * <p><b>Deliberately not the manager's banner.</b> That one is a titled block with an icon and is better;
 * adopting it across twenty Tower screens is a design change nobody asked for and it would move what the e2e
 * selects. `role="alert"` is invisible to every selector, which is what makes it safe to give all twenty at
 * once.
 */
export function ReadFailed({ children, marker, className }: ReadFailedProps) {
  const attributes = marker === undefined ? {} : { [`data-${marker}`]: true }
  return (
    <p
      className={cn(
        'rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-500',
        className,
      )}
      role="alert"
      {...attributes}
    >
      {children}
    </p>
  )
}

interface ReadFailedBlockProps {
  /**
   * The marker stem, without the prefix or the suffix - `"fleet"` gives `data-fleet-failed` on the block
   * and `data-fleet-retry` on the button.
   *
   * <p><b>One stem for both, because all ten copies paired them.</b> `fleet-failed`/`fleet-retry`,
   * `beverage-failed`/`beverage-retry`, `read-failed`/`read-retry` - and a first draft of §9.611 had the
   * retry as a constant `data-read-retry`, which would have renamed six of them.
   */
  marker: string
  /**
   * The value the two markers carry, for the four screens that put the screen's name there instead of in the
   * attribute name: `marker="read" of="design"` gives `data-read-failed="design"`.
   *
   * <p><b>Two conventions, kept rather than reconciled.</b> Six of the ten carry the screen in the attribute
   * name with no value; four carry it as the value. Normalising the four would be tidier and these are
   * **selector names** - `agos-a-ratchets-pattern-is-its-blind-spot` cuts the other way here, and §3.167 is the
   * entry that paid two pixels rather than move a marker.
   */
  of?: string
  /** Bold and red, above everything. Varies on all ten; nine end *sa nepodarilo načítať*. */
  title: string
  /**
   * An icon before the title, which exactly **one** of the ten has.
   *
   * <p><b>A prop rather than a normalisation, and the measurement is why it exists at all.</b> §3.167
   * described the manager's banner as *"a titled block with an icon"* - generalising from that one file.
   * `VehicleFleetPage` carries an `AlertTriangle`; the other nine carry none. Giving all ten one would be a
   * visual change nobody asked for inside a refactor, and taking that one away is the same thing pointed the
   * other way. When it is present the title row becomes a flex row, exactly as that file wrote it.
   */
  icon?: ReactNode
  /**
   * The sentence under the title, saying what the screen is **not** showing and why.
   *
   * <p><b>Required, because all ten have one and five of them are the point.</b> *Nuly by neboli skutočné*,
   * *nula po termíne by neznamenala, že je všetko zaplatené*, *Preto tu nie je ani formulár - prázdne polia by
   * sa dali uložiť a prepísať to, čo je nastavené*. A shared component that baked one sentence would delete ten
   * explanations somebody wrote on purpose - §10A.277's rule.
   */
  children: ReactNode
  /**
   * What *Skúsiť znova* does, and **omitted where retrying is not the remedy** - §9.627, FB-936.
   *
   * <p><b>Every one of the original ten refetches</b>, and all of them still pass it, so this is
   * additive. The first caller that does not is §10A.316's refusal: the read failed because the
   * operator's date range holds more records than the screen can add up, and pressing *Skúsiť znova*
   * asks the same question and gets the same answer. The way out is the range picker above the block -
   * this component's own docstring says it *"offers a way out"*, and a second one that does nothing is
   * the control with no question behind it.
   *
   * <p>Omitting it drops the button and with it `data-{marker}-retry`, which is what a spec should
   * assert: a refusal with a retry button is the defect this option exists to prevent.
   */
  onRetry?: () => void
  className?: string
}

/**
 * A read that did not answer, where there is nothing else on the screen - `FRONTEND.md` §9.611, FB-919.
 *
 * <p><b>Ten hand-written copies, and `role="alert"` on none of them.</b> So ten of the manager's error
 * messages were never announced and the operator had to notice red text. FB-918 is the same measurement on the
 * Tower's twenty-one, and this is the larger half: these are the screens the staff use all day.
 *
 * <p><b>Five people had already extracted this, under three names.</b> `ReadFailed({what, retry})` three times
 * byte-identically, plus `TabReadFailed` and `VehicleFleetLoadFailed`, none of which could see the other four.
 * That is five votes for `@agos/ui` rather than a judgement about tidiness.
 *
 * <p><b>Why two components and not one with a `variant`.</b> {@link ReadFailed} is one sentence that sits
 * **beside** a working screen; this **replaces** a form or a list and offers a way out. A `variant` prop would
 * let a caller ask for a titled block with no title, and the difference between them is not styling - it is
 * whether there is anything else to look at.
 *
 * <p><b>And it took the name `ReadFailed` away from three local components that already had it.</b> They were
 * local so nothing was broken, and all three already imported from `@agos/ui`, so that name in any of those
 * import lists was a redeclaration. The extraction deletes all five locals, so the collision goes with them.
 */
export function ReadFailedBlock({
  marker,
  of,
  title,
  icon,
  children,
  onRetry,
  className,
}: ReadFailedBlockProps) {
  /* `data-load-failed` is the one of the three markers any spec selects (`cms-pages.spec.ts`), so it is
   * constant here exactly as it was constant in all ten copies. */
  /* `of ?? true` is the two conventions in one expression: a bare attribute where the screen is in the
   * name, and the screen's own name as the value where it is not. */
  const failed = { [`data-${marker}-failed`]: of ?? true }
  const retry = { [`data-${marker}-retry`]: of ?? true }
  return (
    <div
      className={cn(
        'rounded-2xl border border-red-500/40 bg-red-500/5 px-4 py-4 text-sm space-y-2',
        className,
      )}
      role="alert"
      data-load-failed
      {...failed}
    >
      <p
        className={cn(
          'font-bold text-red-500',
          icon === undefined ? undefined : 'flex items-center gap-1.5',
        )}
      >
        {icon}
        {title}
      </p>
      <p className="text-muted-foreground">{children}</p>
      {onRetry === undefined ? null : (
        <button
          type="button"
          onClick={onRetry}
          {...retry}
          className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground"
        >
          <T>Skúsiť znova</T>
        </button>
      )}
    </div>
  )
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

export type BadgeColor =
  | 'green'
  | 'red'
  | 'amber'
  | 'blue'
  | 'violet'
  | 'zinc'
  | 'orange'
  | 'emerald'
  | 'pink'
  | 'cyan'

/**
 * Fixed palette, ported verbatim. Order status colours are learned by every
 * member of staff — amber means "in preparation" on every screen in the
 * building, and a badge that picks its own colour breaks that.
 */
const BADGE_COLORS: Record<BadgeColor, string> = {
  green: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  emerald: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  red: 'bg-red-500/15 text-red-500 border-red-500/30',
  amber: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  blue: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
  violet: 'bg-violet-500/15 text-violet-500 border-violet-500/30',
  zinc: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  orange: 'bg-orange-500/15 text-orange-500 border-orange-500/30',
  pink: 'bg-pink-500/15 text-pink-500 border-pink-500/30',
  cyan: 'bg-cyan-500/15 text-cyan-500 border-cyan-500/30',
}

interface StatusBadgeProps {
  label: string
  color: BadgeColor
  icon?: LucideIcon
  size?: 'sm' | 'md'
  className?: string
  /**
   * Hover text — §9.177. A badge is two or three words; when those words are a *verdict* they need
   * to be able to say what was compared with what.
   */
  title?: string
}

export function StatusBadge({
  label,
  color,
  icon: Icon,
  size = 'sm',
  title,
  className,
}: StatusBadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-semibold whitespace-nowrap',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        BADGE_COLORS[color],
        className,
      )}
    >
      {Icon && <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />}
      {label}
    </span>
  )
}

// ─── StatCard ────────────────────────────────────────────────────────────────

/**
 * `pink` is *QR Pay*'s, and only its (FRONTEND.md §9.741): every other payment method already owns a colour on the
 * screens that split the takings - cash emerald, the terminal blue, the courier's card orange, online violet - so the
 * method that arrived last takes a hue none of them use, the one the statistics chart chose for it.
 */
export type StatColor = 'primary' | 'blue' | 'emerald' | 'amber' | 'red' | 'violet' | 'orange' | 'pink'

const STAT_COLORS: Record<StatColor, { background: string; text: string }> = {
  primary: { background: 'bg-primary/10', text: 'text-primary' },
  blue: { background: 'bg-blue-500/10', text: 'text-blue-500' },
  emerald: { background: 'bg-emerald-500/10', text: 'text-emerald-500' },
  amber: { background: 'bg-amber-500/10', text: 'text-amber-500' },
  red: { background: 'bg-red-500/10', text: 'text-red-500' },
  violet: { background: 'bg-violet-500/10', text: 'text-violet-500' },
  orange: { background: 'bg-orange-500/10', text: 'text-orange-500' },
  pink: { background: 'bg-pink-500/10', text: 'text-pink-500' },
}

interface StatCardProps {
  label: string
  value: string | number
  sub?: string
  icon?: LucideIcon
  color?: StatColor
  className?: string
  /**
   * A hook for a test that needs this card's figure.
   *
   * Named rather than found by its label: a Slovak label is the thing §8 keeps byte-for-byte, so a
   * test matching on one breaks when a translation is corrected — and the figure is what such a test
   * is after.
   */
  testId?: string
}

export function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  color = 'primary',
  className,
  testId,
}: StatCardProps) {
  const palette = STAT_COLORS[color]
  return (
    <div className={cn('bg-card border border-border rounded-xl p-4', className)} data-stat={testId}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div
            className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
              palette.background,
            )}
          >
            <Icon className={cn('w-4.5 h-4.5', palette.text)} />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium truncate">
            {label}
          </p>
          <p className={cn('text-lg font-bold leading-tight', palette.text)} data-stat-value>
            {value}
          </p>
          {/* `data-stat-sub` - SAAS.md §3.81. The Tower's own `Figure` carried a `data-figure-note`
            * hook and the e2e read it, so converging on this card needed somewhere for that
            * assertion to land. Purely additive: an attribute, no markup and no class changes, on a
            * component 32 manager screens already render. */}
          {sub && (
            <p className="text-[11px] text-muted-foreground mt-0.5" data-stat-sub>
              {sub}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

/** The loading ring the auth gate and every slow board show. */
export function Spinner({ className }: { className?: string }) {
  return (
    <div
      role="status"
      aria-label={tt('Načítavam...')}
      className={cn(
        'w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin',
        className,
      )}
    />
  )
}
